import type { ProductWithHyperguestHotel } from '@travel-planner/shared/schemas';
import { create } from 'zustand';

export type PendingIntent = 'confirm' | 'reject' | 'manual';

export type PendingMatch = {
  intent: PendingIntent;
  hyperGuestHotelId: number | null;
  unconfirmedHyperGuestId?: number | null;
  // For a freshly-queued MANUAL match there is no server row in the middle
  // column yet, so we keep the resolved product+HG pair to render a synthetic
  // pending row at the top of the Matches tab (dashed border) before Save —
  // mirrors legacy HotelMapperPage prepending `{ productHotel, staticHotel }`
  // to its local `matched` array. This is an overlay view-model, not a copy
  // of query truth (the rows have left the left/right panes once matched).
  preview?: ProductWithHyperguestHotel;
};

type State = {
  pending: Map<string, PendingMatch>;
};

type Actions = {
  enqueue: (productId: string, match: PendingMatch) => void;
  dequeue: (productId: string) => void;
  clear: () => void;
};

// Local-only overlay for the admin Mapper. Stores intents keyed by
// productId; never copies of product or HG rows. Single Save batches
// everything into `applyMatches`. Cleared on success or Cancel.
export const usePendingMatchesStore = create<State & Actions>((set) => ({
  pending: new Map(),
  enqueue: (productId, match) =>
    set((s) => {
      const next = new Map(s.pending);
      next.set(productId, match);
      return { pending: next };
    }),
  dequeue: (productId) =>
    set((s) => {
      const next = new Map(s.pending);
      next.delete(productId);
      return { pending: next };
    }),
  clear: () => set({ pending: new Map() }),
}));
